import { forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Field } from './Field';
import { cx } from './cx';
import f from './Field.module.css';

interface Common {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /**
   * Classe de mise en page de l'enveloppe Field (flex, largeur, grille).
   * `className` ne stylise que le contrôle lui-même : ne jamais y mettre de `flex`/`width`
   * de disposition, l'enveloppe étant une colonne flex.
   */
  fieldClassName?: string;
}

export type InputProps = InputHTMLAttributes<HTMLInputElement> &
  Common & {
    /** Élément accolé avant le champ, sur la même ligne (ex. pastille de couleur). */
    startAdornment?: ReactNode;
  };

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, className, fieldClassName, startAdornment, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={fieldClassName}>
      {({ id, describedBy, invalid }) => {
        const input = (
          <input
            ref={ref}
            id={id}
            className={cx(f.control, className)}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            required={required}
            {...rest}
          />
        );
        return startAdornment ? (
          <div className={f.withAdornment}>
            {startAdornment}
            {input}
          </div>
        ) : (
          input
        );
      }}
    </Field>
  );
});

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & Common;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, className, fieldClassName, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={fieldClassName}>
      {({ id, describedBy, invalid }) => (
        <textarea
          ref={ref}
          id={id}
          className={cx(f.control, className)}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        />
      )}
    </Field>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> &
  Common & { options: SelectOption[]; placeholder?: string };

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, className, fieldClassName, options, placeholder, ...rest },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={fieldClassName}>
      {({ id, describedBy, invalid }) => (
        <select
          ref={ref}
          id={id}
          className={cx(f.control, className)}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          required={required}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled={required}>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});
